import api from '../../utils/api';
import history from '../../utils/history';
import { alertError, alertSuccess } from './alert';
import { SHOPIFY_RETAILERS_FINISHED } from '../constants/shopifyTypes';

export const redirectToAmazonMcf = () => async (dispatch, getState) => {
  let redirectUrlResponse;

  const { supplier } = getState().role;
  redirectUrlResponse = await getSupplierAmazonMcfIntegrationRedirect(supplier.id);

  if (redirectUrlResponse && redirectUrlResponse.data) {
    window.location.href = redirectUrlResponse.data.url;
  } else {
    dispatch(alertError({ message: 'Error redirecting to Shopify, please try again' }));
  }
};

export const postIntegrationAmazonMcf = (params) => async (dispatch) => {
  const payload = params;
  let url;

  if (!payload.spapi_oauth_code || !payload.selling_partner_id || !payload.state) {
    return dispatch(alertError({ message: 'Missing params in request' }));
  }

  url = `/integrations/amazon_mcf/authorized`;

  let supplierId;
  try {
    const response = await api.get(url, {
      params,
    });
    if (!response.data.success) {
      dispatch(alertError({ message: 'Error integrating amazon mcf, please try again' }));
    }

    supplierId = response.data.supplierId;
  } catch (e) {
    dispatch(alertError(e));
  } finally {
    history.push(`/suppliers/${supplierId}/admin/warehouses`);
  }
};

export const redirectToShopifyRetailer = (shopName) => async (dispatch, getState) => {
  let redirectUrlResponse;

  const { retailer } = getState().role;
  redirectUrlResponse = await getRetailerShopifyIntegrationRedirect(retailer.id, shopName);

  if (redirectUrlResponse && redirectUrlResponse.data) {
    window.location.href = redirectUrlResponse.data.url;
  } else {
    dispatch(alertError({ message: 'Error redirecting to Shopify, please try again' }));
  }
};

export const redirectToShopifySupplier = (storeName) => async (dispatch, getState) => {
  let redirectUrlResponse;

  const { supplier } = getState().role;
  redirectUrlResponse = await getSupplierShopifyIntegrationRedirect(supplier.id, storeName);

  if (redirectUrlResponse && redirectUrlResponse.data) {
    window.location.href = redirectUrlResponse.data.url;
  } else {
    dispatch(alertError({ message: 'Error redirecting to Shopify, please try again' }));
  }
};

export const redirectToEbay = (marketplaceId) => async (dispatch, getState) => {
  const { retailer } = getState().role;

  const redirectUrlResponse = await getEbayIntegrationRedirect(retailer.id, marketplaceId);
  if (redirectUrlResponse && redirectUrlResponse.data) {
    window.location.href = redirectUrlResponse.data.url;
  } else {
    dispatch(alertError({ message: 'Error redirecting to Ebay, please try again' }));
  }
};

export const redirectToGoogle = () => async (dispatch, getState) => {
  const { retailer } = getState().role;

  const redirectUrlResponse = await getGoogleIntegrationRedirect(retailer.id);
  if (redirectUrlResponse && redirectUrlResponse.data) {
    window.location.href = redirectUrlResponse.data.url;
  } else {
    dispatch(alertError({ message: 'Error redirecting to Google, please try again' }));
  }
};

export const redirectToShipBob = () => async (dispatch, getState) => {
  const { supplier } = getState().role;

  const redirectUrlResponse = await getShipBobIntegrationRedirect(supplier.id);
  if (redirectUrlResponse?.data) {
    window.location.href = redirectUrlResponse.data.url;
  } else {
    dispatch(alertError({ message: 'Error redirecting to ShipBob, please try again' }));
  }
};

export const redirectToNetSuite = (accountId, locationId) => async (dispatch, getState) => {
  const { supplier } = getState().role;

  const params = new URLSearchParams({
    supplier_id: supplier.id,
    account_id: accountId,
    location_id: locationId,
  });

  try {
    const redirectUrlResponse = await api.get(`/integrations/netsuite/authorize?${params}`);

    const data = redirectUrlResponse?.data;
    if (!data?.success) {
      if (data?.message) {
        dispatch(alertError({ message: data.message }));
      } else {
        dispatch(alertError({ message: 'Error redirecting to NetSuite, please try again' }));
      }
    } else {
      window.location.href = data.url;
    }
  } catch (e) {
    dispatch(alertError({ message: 'Error redirecting to NetSuite, please try again' }));
  }
};

export const postIntegrationNetSuite = (params) => async (dispatch, getState) => {
  const { supplier } = getState().role;

  try {
    const response = await api.post(`/integrations/netsuite/authorized`, params);
    if (!response.data?.success) {
      dispatch(alertError({ message: 'Error integrating NetSuite, please try again' }));
    } else {
      dispatch(alertSuccess(`NetSuite warehouse was connected successfully`));
      history.push(`/suppliers/${supplier.id}/admin/warehouses`);
    }
  } catch (e) {
    dispatch(alertError(e));
  }
};

export const postIntegrationShopifyRetailer = (params) => async (dispatch, getState) => {
  try {
    const response = await api.post(`/shopify/confirm`, params);
    if (response?.data?.success) {
      const { retailerId, updatingScopesOnly } = response.data;
      if (updatingScopesOnly) {
        window.close();
      } else {
        dispatch({ type: SHOPIFY_RETAILERS_FINISHED });
        history.push(`/retailers/${retailerId}/admin`);
      }
    } else {
      dispatch(alertError('Error integrating shopify retailer, please try again'));
    }
  } catch (e) {
    dispatch(alertError(e));
  }
};

export const postIntegrationShopifySupplier = (params) => async (dispatch) => {
  const supplierId = params.state;
  if (!supplierId) {
    return dispatch(alertError({ message: 'No supplier id in request' }));
  }

  try {
    const response = await api.post(`/integrations/shopify/warehouse?supplier_id=${supplierId}`, params);
    if (!response.data.success) {
      dispatch(alertError({ message: 'Error integrating shopify, please try again' }));
    }
  } catch (e) {
    dispatch(alertError(e));
  } finally {
    history.push(`/suppliers/${supplierId}/admin/warehouses`);
  }
};

export const postIntegrationEbay = (params) => async (dispatch) => {
  const { state, code } = params;
  const [retailerId, marketplaceId] = state.split(',');

  if (!retailerId || !marketplaceId) {
    return dispatch(alertError({ message: `No retailerId or marketplaceId in state: ${state}` }));
  }

  if (!code) {
    return dispatch(alertError({ message: 'No code in request' }));
  }

  try {
    const response = await api.post(`/retailers/${retailerId}/integrations/ebay?marketplace_id=${marketplaceId}`, {
      code,
    });
    if (!response.data.success) {
      dispatch(alertError({ message: 'Error integrating ebay, please try again' }));
    }
  } catch (e) {
    dispatch(alertError(e));
  } finally {
    history.push(`/retailers/${retailerId}/admin`);
  }
};

export const postIntegrationGoogle = (params) => async (dispatch) => {
  const retailerId = params.state;

  if (!retailerId) {
    return dispatch(alertError({ message: 'No retailerId in request' }));
  }

  if (!params.code) {
    return dispatch(alertError({ message: 'No code in request' }));
  }

  try {
    const response = await api.post(`/retailers/${retailerId}/integrations/google`, params);
    if (!response.data.success) {
      dispatch(alertError({ message: 'Error integrating google, please try again' }));
    }
  } catch (e) {
    dispatch(alertError(e));
  } finally {
    history.push(`/retailers/${retailerId}/admin`);
  }
};

export const postIntegrationShipBob = (params) => async (dispatch, getState) => {
  const { supplier } = getState().role;

  try {
    const response = await api.post(`/integrations/ship_bob?state=${params.state}&code=${params.code}`, {});
    if (!response.data && response.data.success) {
      dispatch(alertError({ message: 'Error integrating ShipBob, please try again' }));
    }
  } catch (e) {
    dispatch(alertError(e));
  } finally {
    history.push(`/suppliers/${supplier.id}/admin/warehouses`);
  }
};

let cancelGetRetailerShopifyIntegrationRedirect;
export const getRetailerShopifyIntegrationRedirect = async (retailerId, shopName) => {
  if (cancelGetRetailerShopifyIntegrationRedirect) {
    cancelGetRetailerShopifyIntegrationRedirect('Cancel getRetailerShopifyIntegrationRedirect');
  }

  const response = await api.get(`/retailers/${retailerId}/integrations/shopify?shop=${shopName}`, {
    cancelToken: new api.CancelToken((c) => (cancelGetRetailerShopifyIntegrationRedirect = c)),
  });
  return response;
};

let cancelGetSupplierShopifyIntegrationRedirect;
export const getSupplierShopifyIntegrationRedirect = async (supplierId, storeName) => {
  if (cancelGetSupplierShopifyIntegrationRedirect) {
    cancelGetSupplierShopifyIntegrationRedirect('Cancel getSupplierShopifyIntegrationRedirect');
  }

  return api.get(`/integrations/shopify/warehouse?supplier_id=${supplierId}&store=${storeName}`, {
    cancelToken: new api.CancelToken((c) => (cancelGetSupplierShopifyIntegrationRedirect = c)),
  });
};

export const getSupplierAmazonMcfIntegrationRedirect = async (supplierId) => {
  if (cancelGetSupplierShopifyIntegrationRedirect) {
    cancelGetSupplierShopifyIntegrationRedirect('Cancel getSupplierShopifyIntegrationRedirect');
  }

  return api.get(`/integrations/amazon_mcf/warehouse?supplier_id=${supplierId}`, {
    cancelToken: new api.CancelToken((c) => (cancelGetSupplierShopifyIntegrationRedirect = c)),
  });
};

let cancelGetEbayIntegrationRedirect;
export const getEbayIntegrationRedirect = async (retailerId, marketplaceId) => {
  if (cancelGetEbayIntegrationRedirect) {
    cancelGetEbayIntegrationRedirect('Cancel getEbayIntegrationRedirect');
  }

  return api.get(`/retailers/${retailerId}/integrations/ebay?marketplace_id=${marketplaceId}`, {
    cancelToken: new api.CancelToken((c) => (cancelGetEbayIntegrationRedirect = c)),
  });
};

let cancelGetGoogleIntegrationRedirect;
export const getGoogleIntegrationRedirect = async (retailerId) => {
  if (cancelGetGoogleIntegrationRedirect) {
    cancelGetGoogleIntegrationRedirect('Cancel getGoogleIntegrationRedirect');
  }

  return api.get(`/retailers/${retailerId}/integrations/google`, {
    cancelToken: new api.CancelToken((c) => (cancelGetGoogleIntegrationRedirect = c)),
  });
};

let cancelGetShipBobIntegrationRedirect;
export const getShipBobIntegrationRedirect = async (supplierId) => {
  if (cancelGetShipBobIntegrationRedirect) {
    cancelGetShipBobIntegrationRedirect('Cancel getShipBobIntegrationRedirect');
  }

  return api.get(`/integrations/ship_bob/authorizeUrl?supplier_id=${supplierId}`, {
    cancelToken: new api.CancelToken((c) => (cancelGetShipBobIntegrationRedirect = c)),
  });
};
